import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Shield, FileText, Building, Search, Package } from 'lucide-react';
import { services } from '../data/mockData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = { Shield, FileText, Building, Search, Package };

const OurServicesSection = ({ onOpenContactForm }) => {
  const header = useScrollAnimation({ threshold: 0.2 });
  const regularServices = services.filter(s => !s.highlighted);
  const highlightedService = services.find(s => s.highlighted);

  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#030308] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        {/* Centered compact header */}
        <div ref={header.ref} className={`text-center max-w-xl mx-auto mb-14 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="text-xs tracking-[0.2em] uppercase text-amber-500 mb-3 block font-bold">What We Do</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Complete Brand Protection Services
          </h2>
          <p className="text-sm md:text-base text-slate-400 leading-relaxed">
            Every service handled by licensed trademark attorneys. Not bots. Not templates. Real legal professionals.
          </p>
        </div>

        {/* Bento Grid: Row 1 = 2 large cards, Row 2 = 2 regular + 1 highlighted */}
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          {regularServices.slice(0, 2).map((service, idx) => {
            const Icon = iconMap[service.icon];
            return <LargeServiceCard key={service.id} service={service} Icon={Icon} idx={idx} onOpenContactForm={onOpenContactForm} />;
          })}
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {regularServices.slice(2).map((service, idx) => {
            const Icon = iconMap[service.icon];
            return <CompactServiceCard key={service.id} service={service} Icon={Icon} idx={idx + 2} onOpenContactForm={onOpenContactForm} />;
          })}
          {highlightedService && (
            <HighlightedCard service={highlightedService} Icon={iconMap[highlightedService.icon]} onOpenContactForm={onOpenContactForm} />
          )}
        </div>
      </div>
    </section>
  );
};

const LargeServiceCard = ({ service, Icon, idx, onOpenContactForm }) => {
  const card = useScrollAnimation({ threshold: 0.15 });
  return (
    <div
      ref={card.ref}
      data-testid={`service-card-${service.id}`}
      className={`group icon-shift relative rounded-xl p-8 bg-[#0A0A12] border border-white/[0.06] card-hover-premium card-glow-border scroll-fade-up scroll-delay-${idx + 1} ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      <div className="flex items-start gap-5">
        <div className="icon-container w-12 h-12 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0 transition-colors">
          {Icon && <Icon className="w-6 h-6 text-indigo-400 transition-colors" />}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500/70 block mb-1">{service.subtitle}</span>
          <h3 className="text-lg font-bold tracking-tight text-white mb-2 group-hover:text-amber-400 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {service.title}
          </h3>
          <p className="text-sm text-slate-400 leading-relaxed mb-4">{service.description}</p>
          <div className="flex flex-wrap gap-1.5 mb-4">
            {service.features.map((f, i) => (
              <span key={i} className="text-[11px] text-slate-500 bg-white/[0.04] border border-white/[0.06] px-2.5 py-1 rounded-md">{f}</span>
            ))}
          </div>
          <Button data-testid={`service-cta-${service.id}`} onClick={onOpenContactForm} variant="ghost" className="text-indigo-400 hover:text-amber-400 hover:bg-transparent p-0 h-auto font-bold text-sm uppercase tracking-wider">
            Get Started <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const CompactServiceCard = ({ service, Icon, idx, onOpenContactForm }) => {
  const card = useScrollAnimation({ threshold: 0.15 });
  return (
    <div
      ref={card.ref}
      data-testid={`service-card-${service.id}`}
      className={`group icon-shift relative rounded-xl p-6 bg-[#0A0A12] border border-white/[0.06] card-hover-premium card-glow-border scroll-fade-up scroll-delay-${(idx % 3) + 1} ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      <div className="icon-container w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center mb-4 transition-colors">
        {Icon && <Icon className="w-5 h-5 text-indigo-400 transition-colors" />}
      </div>
      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500/70 block mb-1">{service.subtitle}</span>
      <h3 className="text-lg font-bold tracking-tight text-white mb-2 group-hover:text-amber-400 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {service.title}
      </h3>
      <p className="text-sm text-slate-400 leading-relaxed mb-4">{service.description}</p>
      <Button data-testid={`service-cta-${service.id}`} onClick={onOpenContactForm} variant="ghost" className="text-indigo-400 hover:text-amber-400 hover:bg-transparent p-0 h-auto font-bold text-xs uppercase tracking-wider">
        Get Started <ArrowRight className="ml-1.5 w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
      </Button>
    </div>
  );
};

const HighlightedCard = ({ service, Icon, onOpenContactForm }) => {
  const card = useScrollAnimation({ threshold: 0.15 });
  return (
    <div
      ref={card.ref}
      data-testid={`service-card-${service.id}`}
      className={`group relative rounded-xl overflow-hidden card-hover-premium scroll-scale-up scroll-delay-3 ${card.isVisible ? 'scroll-visible' : ''}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-700" />
      <div className="relative z-10 p-6 h-full flex flex-col">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
            {Icon && <Icon className="w-5 h-5 text-white" />}
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">{service.subtitle}</span>
        </div>
        <h3 className="text-lg font-bold tracking-tight text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {service.title}
        </h3>
        <p className="text-sm text-white/70 leading-relaxed mb-4 flex-1">{service.description}</p>
        <Button data-testid={`service-cta-${service.id}`} onClick={onOpenContactForm} className="bg-white text-indigo-700 hover:bg-white/90 font-bold px-5 py-2 text-xs rounded-md uppercase tracking-wider w-fit">
          Enroll Now <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

export default OurServicesSection;
